import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

export interface AttributeRow {
  key: string;
  value: string;
}

export function emptyAttributeRow(): AttributeRow {
  return { key: '', value: '' };
}

interface AttributesEditorProps {
  rows: AttributeRow[];
  onChange: (rows: AttributeRow[]) => void;
}

/** Dynamic key-value editor for a product's free-form specifications. */
export function AttributesEditor({ rows, onChange }: AttributesEditorProps) {
  function updateRow(index: number, patch: Partial<AttributeRow>) {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function removeRow(index: number) {
    onChange(rows.filter((_, i) => i !== index));
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">Attributes</label>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onChange([...rows, emptyAttributeRow()])}
          data-testid="add-attribute-button"
        >
          Add attribute
        </Button>
      </div>
      {rows.length === 0 && <p className="text-xs text-gray-500">No attributes.</p>}
      {rows.map((row, index) => (
        <div
          key={index}
          className="grid grid-cols-1 gap-2 rounded-md border border-gray-200 p-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end"
          data-testid="attribute-row"
        >
          <Input
            label="Name"
            value={row.key}
            onChange={(e) => updateRow(index, { key: e.target.value })}
            placeholder="voltage"
            data-testid="attribute-key-input"
          />
          <Input
            label="Value"
            value={row.value}
            onChange={(e) => updateRow(index, { value: e.target.value })}
            placeholder="220V"
            data-testid="attribute-value-input"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => removeRow(index)}
            data-testid="remove-attribute-button"
          >
            Remove
          </Button>
        </div>
      ))}
    </div>
  );
}
